import React, { useState } from 'react';
import {
  Card,
  FormLayout,
  TextField,
  Button,
  Text,
  BlockStack,
} from '@shopify/polaris';
import { useAuth } from '../contexts/AuthContext';
import { useClient } from '../contexts/ClientContext';
import { useNavigate } from 'react-router-dom';
import ApiErrorBanner from '../components/ApiErrorBanner';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  const { login } = useAuth();
  const { clearSelectedClient } = useClient();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setLoginError(null);

    try {
      const result = await login(email, password);
      if (result.success) {
        // Clear client selection for support managers to force client selection
        if (result.user?.role === 'support_manager') {
          clearSelectedClient();
        }
        navigate('/');
      } else {
        setLoginError(result.error || 'Invalid email or password');
      }
    } catch (error: any) {
      console.error('Login error:', error);
      setLoginError(error?.response?.data?.error || 'Login failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };


  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      backgroundColor: '#f6f6f7',
      padding: '20px'
    }}>
      <div style={{ maxWidth: '400px', width: '100%' }}>
        <Card>
          <div style={{ padding: '2rem' }}>
            <BlockStack gap="500">
              <div style={{ textAlign: 'center' }}>
                <Text variant="headingLg" as="h1">
                  Welcome to Selldo AI Transcript
                </Text>
                <Text variant="bodyMd" as="p" tone="subdued">
                  Sign in to your account
                </Text>
              </div>

              <ApiErrorBanner
                error={loginError}
                title="Login Failed"
                onDismiss={() => setLoginError(null)}
              />

              <form onSubmit={handleSubmit}>
                <FormLayout>
                  <TextField
                    label="Email"
                    type="email"
                    value={email}
                    onChange={setEmail}
                    autoComplete="email"
                    error={loginError ? ' ' : undefined}
                  />
                  <TextField
                    label="Password"
                    type="password"
                    value={password}
                    onChange={setPassword}
                    autoComplete="current-password"
                    error={loginError ? ' ' : undefined}
                  />
                  <Button
                    submit
                    variant="primary"
                    fullWidth
                    loading={isSubmitting}
                    disabled={!email || !password}
                  >
                    Sign In
                  </Button>
                </FormLayout>
              </form>

              {process.env.NODE_ENV === 'development' && (
                <div style={{ textAlign: 'center' }}>
                  <Text variant="bodySm" as="p" tone="subdued">
                    Demo Credentials (dev only):
                  </Text>
                  <Text variant="bodySm" as="p" tone="subdued">
                    Email: admin@sample.com
                  </Text>
                  <Text variant="bodySm" as="p" tone="subdued">
                    Password: password123
                  </Text>
                </div>
              )}
            </BlockStack>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default Login;
